/**
 * WebOffersPageViewModel - Web Platform Adapter
 *
 * Web-specific adapter for OffersPageViewModel
 * Integrates with React Router and web platform services
 */

import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { createOffersPageViewModel } from '@restaurant/viewmodels'
import { createWebPlatformServices, WebOffersPageService } from '@restaurant/viewmodels'
import { useRestaurantStore, useRewardsStore } from '@restaurant/core'

/**
 * Web-specific OffersPageViewModel hook
 *
 * Creates the platform-agnostic OffersPageViewModel with web-specific services
 * and dependencies. Integrates with existing web infrastructure.
 */
export const useWebOffersPageViewModel = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const restaurantStore = useRestaurantStore()
  const availableOffers = useRewardsStore((s) => s.availableOffers)

  // Memoize services to prevent recreation on every render
  const platformServices = useMemo(() => {
    const services = createWebPlatformServices(navigate, location)

    // Enhanced platform services with restaurant context
    return {
      ...services,
      // Override getCurrentRestaurantId to use actual restaurant store
      getCurrentRestaurantId: () => {
        return restaurantStore.currentRestaurant?.id || 'default-restaurant-id'
      },
    }
  }, [navigate, location, restaurantStore.currentRestaurant?.id])

  // Memoize dependencies — pass store getter so the service reads real offers
  const dependencies = useMemo(() => ({
    offersPageService: new WebOffersPageService(() => useRewardsStore.getState().availableOffers),
  }), [])

  // Create and return the platform-agnostic ViewModel
  const OffersPageViewModelHook = useMemo(
    () => createOffersPageViewModel(platformServices, dependencies),
    [platformServices, dependencies]
  )

  const viewModel = OffersPageViewModelHook()

  // Re-load offers when the rewardsStore is populated (handles race with useOffersData)
  useEffect(() => {
    if (availableOffers.length > 0) {
      viewModel.loadOffers()
    }
  }, [availableOffers])

  return viewModel
}